<template>
	<v-container fluid class="empresas-page pa-8 pt-12">
		<div class="empresas-header">
			<div>
				<h1 class="empresas-title">Empresas</h1>
				<p class="empresas-subtitle">Cadastre e mantenha os dados das empresas em um único lugar.</p>
			</div>
			<v-btn class="empresas-new-btn" depressed @click="abrirCadastro()">
				<v-icon left size="18">mdi-plus</v-icon>
				Cadastrar empresa
			</v-btn>
		</div>

		<v-card class="empresas-table-card mt-6" outlined>
			<v-data-table
				class="empresas-table"
				:headers="headers"
				:items="empresas"
				:loading="loading"
				loading-text="Carregando empresas..."
				no-data-text="Nenhuma empresa cadastrada"
				:items-per-page="10"
				:footer-props="{
					itemsPerPageText: 'Itens por Pagina',
					itemsPerPageOptions: [10, 15, 20],
					pageText: '{0}-{1} de {2}'
				}"
			>
				<template v-slot:[`item.razao_social`]='{ item }'>
					<div class="empresas-cell-main empresas-cell-main--nowrap empresas-cell-main--single">
						<strong>{{ item.razao_social || '-' }}</strong>
					</div>
				</template>

				<template v-slot:[`item.cnpj`]='{ item }'>
					<span class="empresas-nowrap">{{ item.cnpj || '-' }}</span>
				</template>

				<template v-slot:[`item.regime_tributario`]='{ item }'>
					<span class="empresas-chip empresas-chip--nowrap">{{ item.regime_tributario || '-' }}</span>
				</template>

				<template v-slot:[`item.responsavel`]='{ item }'>
					<div class="empresas-cell-main">
						<strong>{{ item.nome_responsavel || '-' }}</strong>
						<span>{{ item.cpf_responsavel || '-' }}</span>
					</div>
				</template>

				<template v-slot:[`item.acoes`]='{ item }'>
					<div class="empresas-actions">
						<v-btn icon small @click="abrirCadastro(item)">
							<v-icon color="#10b981" size="18">mdi-pencil-outline</v-icon>
						</v-btn>
						<v-btn icon small @click="abrirExclusao(item)">
							<v-icon color="#ef4444" size="18">mdi-delete-outline</v-icon>
						</v-btn>
					</div>
				</template>
			</v-data-table>
		</v-card>

		<CadastroEmpresaDialog
			v-model="cadastroDialogOpen"
			:empresa="empresaEmEdicao"
			@save="salvarEmpresa"
		/>

		<v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
			<v-card class="empresas-dialog" outlined>
				<v-card-title class="empresas-dialog__title">
					<span>Excluir empresa</span>
					<v-spacer />
					<v-btn icon :disabled="deleting" @click="deleteDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="empresas-confirmacao__texto">
						Tem certeza que deseja excluir <strong>{{ empresaExclusao.razao_social || '-' }}</strong>?
					</div>
					<div class="empresas-confirmacao__aviso">Esta ação não pode ser desfeita.</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="deleting" @click="deleteDialogOpen = false">Cancelar</v-btn>
					<v-btn class="empresas-delete-btn" depressed :loading="deleting" :disabled="deleting" @click="confirmarExclusao">
						Excluir
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import CadastroEmpresaDialog from './cadastroEmpresaDialog.vue';
import empresasServices from './empresasServices';

const HEADERS = [
	{ text: 'Regime tributário', value: 'regime_tributario', sortable: false, width: '260px' },
	{ text: 'CNPJ', value: 'cnpj', sortable: false, width: '170px' },
	{ text: 'Empresa', value: 'razao_social', sortable: false, width: '280px' },
	{ text: 'Responsável', value: 'responsavel', sortable: false, width: '220px' },
	{ text: 'Ações', value: 'acoes', sortable: false, align: 'center', width: '90px' },
];

export default {
	name: 'ListagemEmpresas',

	components: {
		CadastroEmpresaDialog,
	},

	data() {
		return {
			headers: HEADERS,
			loading: false,
			deleting: false,
			cadastroDialogOpen: false,
			deleteDialogOpen: false,
			empresas: [],
			empresaEmEdicao: null,
			empresaExclusao: {},
		};
	},

	created() {
		this.carregarEmpresas();
	},

	methods: {
		async carregarEmpresas() {
			this.loading = true;

			try {
				const response = await empresasServices.listar();
				this.empresas = Array.isArray(response?.data) ? response.data : [];
			} catch (error) {
				this.empresas = [];
				this.$toasted?.error(error?.response?.data?.error || 'Não foi possível carregar as empresas.');
			} finally {
				this.loading = false;
			}
		},

		abrirCadastro(item = null) {
			this.empresaEmEdicao = item ? { ...item } : null;
			this.cadastroDialogOpen = true;
		},

		async salvarEmpresa(payload) {
			try {
				if (this.empresaEmEdicao?.id) {
					await empresasServices.atualizar(this.empresaEmEdicao.id, payload);
					this.$toasted?.show('Empresa atualizada com sucesso.');
				} else {
					await empresasServices.criar(payload);
					this.$toasted?.show('Empresa cadastrada com sucesso.');
				}

				this.cadastroDialogOpen = false;
				this.empresaEmEdicao = null;
				await this.carregarEmpresas();
			} catch (error) {
				this.$toasted?.error(error?.response?.data?.error || 'Não foi possível salvar a empresa.');
			}
		},

		abrirExclusao(item) {
			this.empresaExclusao = item || {};
			this.deleteDialogOpen = true;
		},

		async confirmarExclusao() {
			if (!this.empresaExclusao?.id) {
				return;
			}

			this.deleting = true;

			try {
				await empresasServices.excluir(this.empresaExclusao.id);
				this.$toasted?.show('Empresa excluída com sucesso.');
				this.deleteDialogOpen = false;
				this.empresaExclusao = {};
				await this.carregarEmpresas();
			} catch (error) {
				this.$toasted?.error(error?.response?.data?.error || 'Não foi possível excluir a empresa.');
			} finally {
				this.deleting = false;
			}
		},
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.empresas-page {
	background: linear-gradient(180deg, #f8fbff 0%, #f3f7fb 100%);
	min-height: 100%;
	font-family: "Inter", sans-serif;
}

.empresas-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 16px;
	flex-wrap: wrap;
}

.empresas-title {
	color: #10b981;
	font-size: 22px;
	font-weight: 800;
	line-height: 1.2;
	margin: 0;
}

.empresas-subtitle {
	color: #7a8ca6;
	font-size: 13px;
	margin: 6px 0 0;
}

.empresas-new-btn {
	background: linear-gradient(135deg, #18c58b 0%, #12b57a 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 14px;
	text-transform: none;
	padding: 0 18px !important;
	box-shadow: 0 16px 32px rgba(18, 181, 122, 0.22) !important;
}

.empresas-table-card,
.empresas-dialog {
	border-radius: 24px !important;
	border: 1px solid #e6ebf1 !important;
	background: rgba(255, 255, 255, 0.96) !important;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.08) !important;
}

.empresas-cell-main {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.empresas-cell-main--nowrap {
	white-space: nowrap;
}

.empresas-cell-main--single {
	display: block;
}

.empresas-cell-main strong {
	color: #1f2a44;
	font-weight: 700;
}

.empresas-cell-main span {
	color: #7a8ca6;
	font-size: 12px;
	line-height: 1.4;
}

.empresas-chip {
	display: inline-flex;
	align-items: center;
	padding: 6px 10px;
	border-radius: 999px;
	background: #ecfdf5;
	color: #0f9f6e;
	font-size: 12px;
	font-weight: 700;
	line-height: 1.2;
}

.empresas-chip--nowrap,
.empresas-nowrap {
	white-space: nowrap;
}

.empresas-actions {
	display: flex;
	justify-content: center;
	gap: 4px;
}

.empresas-dialog__title {
	padding: 24px 24px 18px;
	font-size: 20px;
	font-weight: 700;
	color: #1f2a44;
}

.empresas-confirmacao__texto {
	color: #1f2a44;
	font-size: 14px;
	line-height: 1.6;
}

.empresas-confirmacao__aviso {
	font-size: 13px;
	color: #7a8ca6;
	margin-top: 10px;
}

.empresas-delete-btn {
	background: linear-gradient(135deg, #f87171 0%, #ef4444 100%) !important;
	color: #fff !important;
	font-weight: 700;
	border-radius: 12px;
	text-transform: none;
}
</style>